import { useEffect, useState } from 'react';
import { ExternalLink, MapPin, Trash2, Video } from 'lucide-react';
import type { CalendarItem, EventInput } from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { SelectField, TextField } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { useEventMutation, useWorkspaces } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { formatDue } from '../../lib/dates.ts';

const plusHour = (t: string) => {
  const [h, m] = t.split(':').map(Number) as [number, number];
  return `${String(Math.min(23, h + 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Create/edit a Unitly event; Google events open read-only (they are edited in Google Calendar). */
export function EventSheet({ state, onClose }: { state: { item?: CalendarItem; date?: string } | null; onClose: () => void }) {
  const { orgId, today } = useOrgCtx();
  const workspaces = useWorkspaces(orgId).data ?? [];
  const mutate = useEventMutation(orgId);
  const toast = useToast();
  const item = state?.item;
  const readOnly = Boolean(item && !item.canEdit);
  const [v, setV] = useState<Required<EventInput> & { allDay: boolean }>(() => blank(today));

  function blank(date: string) {
    return { title: '', date, endDate: null, startTime: '10:00', endTime: '11:00', location: null, description: null, workspaceId: null, allDay: false };
  }
  useEffect(() => {
    if (!state) return;
    if (item) {
      setV({
        title: item.title, date: item.startDate, endDate: item.endDate !== item.startDate ? item.endDate : null,
        startTime: item.startTime, endTime: item.endTime, location: item.location, description: null,
        workspaceId: item.workspaceId, allDay: item.allDay,
      });
    } else setV(blank(state.date ?? today));
    mutate.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const err = mutate.error instanceof ApiError ? mutate.error : null;
  const save = () => {
    const input: EventInput = {
      title: v.title, date: v.date, endDate: v.endDate, workspaceId: v.workspaceId, location: v.location || null, description: v.description || null,
      startTime: v.allDay ? null : v.startTime, endTime: v.allDay ? null : v.endTime,
    };
    mutate.mutate({ id: item?.refId, input }, { onSuccess: () => { toast(item ? 'Evento aggiornato' : `Evento aggiunto · ${formatDue(v.date, v.allDay ? null : v.startTime, today)}`); onClose(); } });
  };

  return (
    <Sheet open={Boolean(state)} onClose={onClose} variant="center" title={item ? (readOnly ? 'Evento' : 'Modifica evento') : 'Nuovo evento'} labelledBy="event-title">
      {readOnly && item ? (
        <div className="form">
          <h3 style={{ margin: 0, fontSize: 22 }}>{item.title}</h3>
          <p style={{ margin: 0, color: 'var(--label-2)' }}>
            {formatDue(item.startDate, item.startTime, today)}{item.endTime ? ` – ${item.endTime}` : ''}{item.endDate !== item.startDate ? ` → ${formatDue(item.endDate, null, today)}` : ''}
          </p>
          {item.calendarName && <p className="hint" style={{ padding: 0 }}>Da Google Calendar · {item.calendarName}. Si modifica da Google Calendar.</p>}
          {item.location && <p style={{ margin: 0, display: 'flex', gap: 6, alignItems: 'center' }}><MapPin size={16} /> {item.location}</p>}
          {item.conferenceUrl && (
            <a className="btn btn-primary" href={item.conferenceUrl} target="_blank" rel="noopener noreferrer"><Video size={17} /> Partecipa con Google Meet <ExternalLink size={14} /></a>
          )}
        </div>
      ) : (
        <form className="form" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <TextField data-autofocus label="Titolo" placeholder="Es. Riunione di redazione" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} error={err?.errors.title} required maxLength={300} />
          <label className="cal-toggle" style={{ justifyContent: 'space-between' }}>
            <span>Tutto il giorno</span>
            <button type="button" role="switch" className="switch" aria-checked={v.allDay} aria-label="Tutto il giorno" onClick={() => setV({ ...v, allDay: !v.allDay })} />
          </label>
          <div className="inline-form">
            <label className="field"><span>{v.allDay ? 'Dal' : 'Data'}</span><span className="date-field"><input className="input" type="date" value={v.date} required onChange={(e) => setV({ ...v, date: e.target.value })} /></span></label>
            {v.allDay ? (
              <label className="field"><span>Al</span><span className="date-field"><input className="input" type="date" value={v.endDate ?? v.date} min={v.date} onChange={(e) => setV({ ...v, endDate: e.target.value === v.date ? null : e.target.value })} /></span></label>
            ) : (
              <>
                <label className="field" style={{ minWidth: 100 }}><span>Inizio</span><span className="date-field"><input className="input" type="time" value={v.startTime ?? ''} required
                  onChange={(e) => setV({ ...v, startTime: e.target.value, endTime: v.endTime && v.endTime > e.target.value ? v.endTime : plusHour(e.target.value) })} /></span></label>
                <label className="field" style={{ minWidth: 100 }}><span>Fine</span><span className="date-field"><input className="input" type="time" value={v.endTime ?? ''} onChange={(e) => setV({ ...v, endTime: e.target.value || null })} /></span></label>
              </>
            )}
          </div>
          <TextField label="Luogo" placeholder="Sala riunioni, indirizzo o link" value={v.location ?? ''} onChange={(e) => setV({ ...v, location: e.target.value })} />
          <SelectField label="Chi lo vede" value={v.workspaceId ?? ''} onChange={(e) => setV({ ...v, workspaceId: e.target.value || null })}>
            <option value="">Tutta l’azienda</option>
            {workspaces.map((w) => <option key={w.id} value={w.id}>Spazio {w.name}</option>)}
          </SelectField>
          {!item && <TextField label="Note" value={v.description ?? ''} onChange={(e) => setV({ ...v, description: e.target.value })} />}
          {err && Object.keys(err.errors).length === 0 && <div className="form-error">{err.message}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            {item && (
              <button type="button" className="btn btn-danger" aria-label="Elimina evento"
                onClick={() => confirm('Eliminare questo evento?') && mutate.mutate({ id: item.refId, remove: true }, { onSuccess: () => { toast('Evento eliminato'); onClose(); } })}>
                <Trash2 size={17} />
              </button>
            )}
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={!v.title.trim() || mutate.isPending}>{item ? 'Salva' : 'Aggiungi evento'}</button>
          </div>
          <p className="hint">Gli eventi creati qui restano in Unitly. La creazione diretta su Google Calendar con link Meet arriva con il permesso di scrittura (prossima fase).</p>
        </form>
      )}
    </Sheet>
  );
}
